/* eslint-disable no-case-declarations */
/* eslint-disable no-param-reassign */
/* eslint-disable no-return-assign */
import {
  getDiscountValue,
  getLeadGenerator,
  getOfferName,
  getOrderType,
  IS_GROUP_MEMBER_VALUES,
  PLAN_CATEGORIES,
  returnV2CalculatedTax,
  SIM_JOURNEY_TYPES,
} from '@smarty-nx/code-utils'
import omit from 'lodash/omit'

import {
  DATA_LAYER_EVENTS,
  DATA_LAYER_SCREEN_TYPES,
  GROUP_INVITE_TYPES,
} from '../constants'
import {
  convertDataAttributeToObjects,
  findPageAttrs,
  PathMapEntry,
  removeTrailingSlash,
  sanitizePathname,
  triggerEvent,
} from '../utils'
import {
  AddonProductObject,
  AssetItem,
  DataLayerEvent,
  DataLayerObject,
  DiscountData,
  ModuleItem,
  PaymentData,
  PortInOption,
  ProductObject,
  Smdl,
  SmdlAssets,
  SmdlGroup,
  SmdlGroupInfo,
  SmdlModule,
  SmdlPlatform,
  SmdlScreen,
  SmdlUser,
  ValueObject,
} from '../types'
import {
  GroupV1PortInStatuses,
  Plan,
  PortInDetails,
  SignUpStatus,
} from '@smarty-nx/data'

const { CHECKOUT, HOMEPAGE, PRODUCT_LIST, PRODUCT_DETAILS } =
  DATA_LAYER_SCREEN_TYPES

interface StoredState {
  auth?: { jwt?: string }
  simType?: string
  isGroupOwner: boolean
  isGroupMember: boolean | null
  isMemberUnder16: boolean
  memberNickname: string
  groupId?: number
  groupInvitations?: number
  groupSize?: number
  isVitalsReady?: boolean
  familyPlanGroup: {
    selectedMemberId?: number | null
    data: {
      members: { status: SignUpStatus }[]
    }
  }
}

const getState = (): StoredState => {
  const storageItem =
    localStorage.getItem('state') || sessionStorage.getItem('state')
  return JSON.parse(storageItem || '{}')
}

export const getSimType = () => {
  const state = getState()

  if (state?.auth?.jwt) {
    return state?.simType
  }

  return
}

// TODO Investigate why there is different logic for sessionStorage vs localStorage
export const buildUser = (): SmdlUser => {
  const state = JSON.parse(localStorage.getItem('state') || '{}')
  const sessionState = JSON.parse(sessionStorage.getItem('state') || '{}')
  const offerName = getOfferName(state?.currentPlan)

  if (state && state.auth && state.auth.jwt) {
    const user = {
      status: 'logged-in',
      email: state.email,
      customer: {
        id: state.customerId,
        plan: state.customerPlan,
        plan_status: state.planStatus,
        ...(state.currentPlan && {
          offer_name: offerName,
          // we do not have the name of the property that belongs to voucher in
          //  contentful for this prop yet, so for now just a placeholder here as agreed with
          //  Lydia from DMPG team
          offer_type: 'no offer',
        }),
        order_created_at: state.orderCreatedAt,
      },
    }

    if (state.actingAs) {
      user.customer.actingAs = state.actingAs
      if (state.currentPlan) {
        user.customer.member_offer_name = offerName
      }
    }

    return user as SmdlUser
  }

  if (sessionState && sessionState.auth && sessionState.auth.jwt) {
    return {
      status: 'logged-in',
      customer: {
        id: sessionState.customerId,
        plan: sessionState.customerPlan,
        plan_status: sessionState.planStatus,
        ...(sessionState.currentPlan && {
          offer_name: offerName,
          offer_type: 'no offer',
        }),
        order_created_at: sessionState.orderCreatedAt,
      },
    }
  } else if (state && state.journey && state.journey.isPaypointJourney) {
    return {
      status: 'logged-out',
      customer: {
        paypoint: 'true',
      },
    } as SmdlUser
  }
  return {
    status: 'logged-out',
  }
}

export const buildUserWithOrderType = (orderType?: string): SmdlUser => {
  const user = buildUser()
  if (user.status !== 'logged-out' && orderType) {
    user.customer.order_type = orderType
  }

  return user
}

export const getPortInOption = (
  details: PortInDetails | null
): PortInOption => {
  if (!details || details.status === GroupV1PortInStatuses.MAYBE_PORT_SKIPPED) {
    return PortInOption.PORT_IN_LATER
  }

  if (details.code_type === 'pac') {
    return details.requested_portin_date
      ? PortInOption.PAC_WITH_DATE
      : PortInOption.PAC_WITHOUT_DATE
  } else {
    return details.requested_portin_date
      ? PortInOption.STAC_WITH_DATE
      : PortInOption.STAC_WITHOUT_DATE
  }
}

export const getIsGroupMemberValue = (
  isOwner: boolean | null,
  isMember: boolean | null
): IS_GROUP_MEMBER_VALUES => {
  switch (true) {
    case isOwner:
      return IS_GROUP_MEMBER_VALUES.YES_OWNER
    case isMember:
      return IS_GROUP_MEMBER_VALUES.YES
    default:
      return IS_GROUP_MEMBER_VALUES.NO
  }
}

// In fact this is not correct helper and should be removed in future
export const getGroupAccountType = (
  isMemberUnder16: boolean,
  memberNickname: string,
  authenticated: boolean
) =>
  !isMemberUnder16 && memberNickname
    ? GROUP_INVITE_TYPES.DEVICE
    : authenticated
    ? // Contract as part of DIGDEV-14422
      GROUP_INVITE_TYPES.GROUP_EXISTING
    : GROUP_INVITE_TYPES.GROUP_ACCOUNT

export const buildGroupInfo = (
  inviteType: string | null = null,
  accountType?: string
): SmdlGroupInfo => {
  const state = getState()
  const authenticated = Boolean(state?.auth && state.auth.jwt)

  const group: SmdlGroup = {
    id: null,
    invitations: 0,
    groupSize: 0,
    isGroupMember: getIsGroupMemberValue(
      state.isGroupOwner,
      state.isGroupMember
    ),
    groupAccountType:
      accountType ||
      getGroupAccountType(
        state.isMemberUnder16,
        state.memberNickname,
        authenticated
      ),
  }

  if (state.groupId) {
    const acceptedInvitationMembers =
      state.familyPlanGroup?.data?.members.filter(
        (member: { status: SignUpStatus }) =>
          member.status !== SignUpStatus.INVITATION_SENT &&
          member.status !== SignUpStatus.SETUP
      ) || []

    const pendingInvitationMembers =
      state.familyPlanGroup?.data?.members.filter(
        (member: { status: SignUpStatus }) =>
          member.status === SignUpStatus.INVITATION_SENT
      ) || []

    group.id = state.groupId
    group.invitations = pendingInvitationMembers.length || 0
    const isViewingOwnMemberDashboard =
      state.isGroupMember && !state.familyPlanGroup?.selectedMemberId
    group.groupSize =
      (acceptedInvitationMembers.length || 0) +
      1 + // +1 for the owner
      (isViewingOwnMemberDashboard ? 1 : 0) // +1 for the member themselves
    group.isOverSixteen = state.isMemberUnder16 ? 'no' : 'yes'
  }

  return {
    group,
    inviteType,
  }
}

let pathMap: Record<string, PathMapEntry>,
  regexPathMap: Record<string, PathMapEntry>
export const setDmpgObject = ({
  pathMap: appPathMap,
  regexPathMap: appRegexPathMap,
  platform,
}: {
  pathMap: Record<string, PathMapEntry>
  regexPathMap: Record<string, PathMapEntry>
  platform: SmdlPlatform
}) => {
  pathMap = appPathMap
  regexPathMap = appRegexPathMap
  if (!window.smDl) {
    window.smDl = {
      events: [],
      platform,
    }
  }
  window.smDl.user = buildUser()
  window.smDl.screen = getScreenFromLocation()
  window.smDl.groupInfo = buildGroupInfo()
  buildEventsBasedOnScreenType(window.smDl.screen, document, false)
  attachAllClickEvents(document, window.smDl)
}

const elements = (
  parent: Document | HTMLElement,
  selector: string
): HTMLElement[] => Array.from(parent.querySelectorAll(selector))

const element = (
  parent: Document | HTMLElement,
  selector: string
): HTMLElement | null => parent.querySelector(selector)

export const findProducts = (
  parentEl: Document | HTMLElement = document
): (ProductObject | AddonProductObject | undefined)[] =>
  elements(parentEl, '[data-product_id]')
    .map(convertDataAttributeToObjects)
    .map(({ product }: DataLayerObject) => product)

const buildDiscount = (discount?: DiscountData) => {
  if (discount) {
    const { displayGross, displayTax } = discount.value
    return {
      discount: {
        lines: [
          {
            ...discount,
            value: {
              displayGross: Number(displayGross),
              displayTax: Number(displayTax),
            },
          },
        ],
      },
    }
  }
  return undefined
}

/**
 * OLD function which uses data attributes in the checkout pages
 * to build a cart object with product data.
 * Replaced by buildCartData in buildEventData.ts
 * */
export const buildCart = (parentEl: Document | HTMLElement) => {
  const cartEl = element(parentEl, '[data-cart_checkout-step]')
  if (cartEl) {
    const { cart, leadGenerator, orderType, selectedPortInOption } =
      convertDataAttributeToObjects(cartEl)

    const productLines = elements(parentEl, '[data-lines_id]')
      .map(convertDataAttributeToObjects)
      .map(({ lines, discount }) => {
        const {
          displayGross,
          displayTax,
          discount: lineDiscount,
        } = lines?.value || {}
        return {
          product: {
            ...lines,
            quantity: Number(lines?.quantity),
            value: {
              displayGross: Number(displayGross),
              displayTax: Number(displayTax),
            },
            ...(lineDiscount && {
              ...lineDiscount,
              value: {
                displayGross: Number(lineDiscount.value.displayGross),
                ...(lineDiscount.value.displayTax && {
                  displayTax: Number(lineDiscount.value.displayTax),
                }),
              },
            }),
          },
          ...buildDiscount(discount),
        }
      })

    return {
      ...cart,
      lines: productLines,
      leadGenerator,
      orderType,
      selectedPortInOption,
      ...(cart?.['value']
        ? {
            value: {
              displayGross: Number((cart['value'] as ValueObject).displayGross),
              displayTax: Number((cart['value'] as ValueObject).displayTax),
            },
          }
        : {}),
    }
  }

  return undefined
}

const buildPaymentArray = (
  parentEl: Document | HTMLElement,
  categoryType?: string
): PaymentData[] => {
  const paymentEl = element(parentEl, '[data-payment-credit]')
  const paymentElPreloaded = element(
    parentEl,
    '[data-payment-credit-preloaded]'
  )
  let payments: PaymentData[] = []
  if (paymentEl) {
    payments = [
      {
        type: 'credit',
        value: {
          displayGross: Number(paymentEl.getAttribute('data-payment-credit')),
        },
      },
      {
        type: paymentEl.getAttribute('data-payment-method'),
        value: {
          displayGross:
            categoryType === PLAN_CATEGORIES.PRELOADED_SIM
              ? 0
              : Number(paymentEl.getAttribute('data-payment-paid')),
        },
      },
    ]
  }
  if (paymentElPreloaded) {
    const item = {
      type: 'credit_preloaded',
      value: {
        displayGross: Number(
          paymentElPreloaded.getAttribute('data-payment-credit-preloaded')
        ),
      },
    }
    payments = Array.isArray(payments) ? payments.concat(item) : [item]
  }
  return payments
}

const buildTransaction = (parentEl: Document | HTMLElement) => {
  const transactionEl = element(parentEl, '[data-transaction_id]')
  if (transactionEl) {
    const { transaction } = convertDataAttributeToObjects(transactionEl)
    return transaction
  }

  return undefined
}

const buildItem = (el: HTMLElement): ModuleItem => ({
  id: el.dataset['itemId'],
  type: el.getAttribute('data-type'),
  position: el.getAttribute('data-position'),
})

const findItem = (
  module: Document | HTMLElement,
  slug?: string
): ModuleItem[] =>
  slug
    ? elements(module, `[data-item-id="${slug}"]`).map(buildItem)
    : elements(module, '[data-item-id]').map(buildItem)

const findModules = (
  parentEl: Document | HTMLElement = document,
  slug?: string
): SmdlModule[] =>
  elements(parentEl, '[data-module-id]').map((el: HTMLElement) => ({
    ...convertDataAttributeToObjects(el).module,
    id: el.getAttribute('data-module-id'),
    items: findItem(el, slug),
  }))

const buildItemWithAssetData = (el: HTMLElement) => {
  const { asset, type, discount } = convertDataAttributeToObjects(el)
  return {
    ...asset,
    ...buildDiscount(discount),
    type,
    id: el.getAttribute('data-item-id'),
  }
}

const findAssets = (
  parentEl: Document | HTMLElement = document,
  slug?: string
) => {
  const itemIdQuery = slug ? `[data-item-id="${slug}"]` : '[data-item-id]'
  return elements(parentEl, itemIdQuery)
    .map(buildItemWithAssetData)
    .reduce((acc, item) => {
      if (item.type) {
        if (!acc[item.type]) {
          acc[item.type] = []
        }
        acc[item.type].push(omit(item, ['type']))
      }
      return acc
    }, {} as AssetItem)
}

export const moduleEvent = (
  container: Document | HTMLElement,
  slug?: string
): DataLayerEvent => ({
  event: DATA_LAYER_EVENTS.MODULE_LOAD,
  modules: findModules(container, slug),
  assets: findAssets(container, slug),
})

const productEvent = (container: Document | HTMLElement): DataLayerEvent => ({
  event: DATA_LAYER_EVENTS.PRODUCT_LOAD,
  products: findProducts(container),
})

const screenEvent = ({ screen, user }: Smdl): DataLayerEvent => {
  const simType = getSimType()

  return {
    event: DATA_LAYER_EVENTS.SCREEN_LOAD,
    screen: { ...screen } as SmdlScreen,
    user,
    groupInfo: buildGroupInfo(),
    ...(simType ? { sim_type: simType } : {}),
  }
}

const removeIfArrayEmpty = (
  event: DataLayerEvent,
  arrayName: keyof Pick<DataLayerEvent, 'modules' | 'products'>
) => {
  if (Array.isArray(event[arrayName]) && event[arrayName].length > 0) {
    return event
  }
  return undefined
}

function compact<T>(array: (T | undefined)[]): T[] {
  return array.filter((value): value is T => value !== undefined)
}

const buildEventsBasedOnScreenType = (
  screen: SmdlScreen | undefined,
  container: Document | HTMLElement,
  pageReady = true
): DataLayerEvent[] => {
  const { type = '', name = '' } = screen || {}
  let events: DataLayerEvent[] = []

  const contentDependentScreenTypes = [CHECKOUT, PRODUCT_DETAILS]
  if (type && !pageReady && contentDependentScreenTypes.includes(type)) {
    return []
  }

  switch (type) {
    case HOMEPAGE:
      return compact([removeIfArrayEmpty(moduleEvent(container), 'modules')])
    case PRODUCT_DETAILS:
      return compact([
        removeIfArrayEmpty(moduleEvent(container), 'modules'),
        removeIfArrayEmpty(productEvent(container), 'products'),
      ]).concat(
        findProducts(container).map((product) => ({
          product,
          event: DATA_LAYER_EVENTS.PRODUCT_VIEW,
        }))
      )
    case CHECKOUT:
      const cart = buildCart(container)
      const categoryType = cart?.lines?.[0]?.product?.category
      const payment = buildPaymentArray(container, categoryType)
      const { selectedPortInOption: selected_port_in_option } = cart || {}

      // Lets us our standard methods for getting these values rather than scraping the data attributes.
      const orderType = getOrderType()
      const leadGenerator = getLeadGenerator()

      if (orderType) {
        if (name === 'plans-confirmation') {
          const transaction = buildTransaction(container)
          if (transaction && transaction.id) {
            if (
              cart?.value &&
              cart?.lines?.[0]?.product?.category ===
                PLAN_CATEGORIES.PRELOADED_SIM
            ) {
              cart.lines[0].product.value.displayGross = 0
              cart.lines[0].product.value.displayTax = 0
              cart.value.displayGross = 0
              cart.value.displayTax = 0
            }

            events = [
              {
                event: DATA_LAYER_EVENTS.TRANSACTION_SUCCESS,
                transaction: {
                  ...transaction,
                  cart: {
                    ...cart,
                    orderType,
                    leadGenerator,
                  },
                  payment,
                  lead_generator: leadGenerator,
                  order_type: orderType,
                  selected_port_in_option,
                },
              },
            ]
          } else {
            events = []
          }
        } else {
          events = []
          if (cart) {
            events.push({
              event: DATA_LAYER_EVENTS.CART_LOAD,
              order_type: SIM_JOURNEY_TYPES.ONLINE_SIM_ORDER_JOURNEY,
              cart,
            })
          }
        }
      }
      return events
    default:
      return events
  }
}

const attachClickEvent = (
  _container: Document | HTMLElement,
  ele: HTMLElement,
  smdlObject: Smdl,
  clickEvent = 'data-dmpg-event-name'
) =>
  (ele.onclick = () =>
    smdlObject.events.push({
      event: ele.getAttribute(clickEvent) || '',
      ...convertDataAttributeToObjects(ele)?.event,
    }))

const attachAllClickEvents = (
  container: Document | HTMLElement,
  smdlObject: Smdl
) => {
  elements(container, '[data-dmpg-event-name]').forEach((el) =>
    attachClickEvent(container, el, smdlObject)
  )

  elements(container, '[data-item-id]').forEach((el) =>
    attachClickEvent(container, el, smdlObject, 'data-item-id')
  )
}

export const getScreenFromLocation = (): SmdlScreen | undefined => {
  const screen = findPageAttrs({
    path: sanitizePathname(removeTrailingSlash(window.location.pathname)),
    pathMap,
    regexPathMap,
  })

  // Only include leadGenerator and orderType for certain screen types
  if (
    screen &&
    [CHECKOUT, PRODUCT_LIST, PRODUCT_DETAILS].includes(screen.type)
  ) {
    screen.lead_generator = getLeadGenerator()
    screen.order_type = getOrderType()
  }
  return screen
}

export const dmpgLayerNotAlreadyInitializedForThisScreen = (
  screen?: SmdlScreen,
  previouslyAttachedScreen?: string | null
) => screen && screen.name !== previouslyAttachedScreen

const completeInitializationForScreen = (
  completedEvents: DataLayerEvent[],
  smdlObject: Smdl,
  container: Document | HTMLElement
) => {
  completedEvents.forEach((completedEvent: DataLayerEvent) =>
    smdlObject.events.push(completedEvent)
  )
  attachAllClickEvents(container, smdlObject)
}

export const findLastScreenEvent = (
  smdlObject: Smdl
): DataLayerEvent | undefined =>
  (smdlObject.events || [])
    .filter((e) => e.event === DATA_LAYER_EVENTS.SCREEN_LOAD)
    .slice(-1)?.[0]

const matchScreen = (a?: SmdlScreen, b?: SmdlScreen) =>
  a?.name === b?.name && a?.type === b?.type

export const addScreenEventIfItChanged = (smdlObject: Smdl) => {
  const lastScreenEvent = findLastScreenEvent(smdlObject)
  if (
    !lastScreenEvent ||
    !matchScreen(smdlObject.screen, lastScreenEvent?.screen)
  ) {
    smdlObject.events.push(screenEvent(smdlObject))
  }
}

const buildDmpgDataAttributeListener = ({
  container,
  smdlObject,
  getScreen,
  skipVitalsCheck = false,
}: {
  container: Document | HTMLElement
  smdlObject: Smdl
  getScreen?: () => SmdlScreen | undefined
  skipVitalsCheck?: boolean
}) => {
  let previouslyAttachedScreen: string | null = null
  return () => {
    const state = getState()
    // Only proceed if data required for screen tracking is already loaded & stored
    // This is not needed for marketing where skipVitalsCheck=true is passed
    // If there is no jwt (user is not logged in), we also continue
    if (!skipVitalsCheck && state?.auth?.jwt && !state.isVitalsReady) {
      return
    }
    smdlObject.screen = getScreen?.() || smdlObject.screen
    smdlObject.user = buildUser()
    if (smdlObject.screen) {
      addScreenEventIfItChanged(smdlObject)
    }
    if (
      dmpgLayerNotAlreadyInitializedForThisScreen(
        smdlObject.screen,
        previouslyAttachedScreen
      )
    ) {
      const completedEvents = buildEventsBasedOnScreenType(
        smdlObject?.screen,
        container
      )

      if (completedEvents?.length > 0) {
        completeInitializationForScreen(completedEvents, smdlObject, container)
        previouslyAttachedScreen = smdlObject?.screen?.name || null
      }
    }
  }
}

export const attach = ({
  container,
  smdlObject,
  getScreen = getScreenFromLocation,
  skipVitalsCheck = false,
}: {
  container: Document | HTMLElement
  smdlObject: Smdl
  getScreen?: () => SmdlScreen | undefined
  skipVitalsCheck?: boolean
}) => {
  const dmpgListener = buildDmpgDataAttributeListener({
    container,
    smdlObject,
    getScreen,
    skipVitalsCheck,
  })
  const observer = new MutationObserver(dmpgListener)
  observer.observe(container, { childList: true, subtree: true })
  return observer
}

const buildForm = (
  container: Document | HTMLElement = document,
  fields?: Record<string, string>[]
) => {
  const form = container.querySelector('form')
  if (form) {
    return {
      id: form.getAttribute('data-form-name'),
      fields,
    }
  }
  return undefined
}

const eventExtras = (
  eventName: string,
  container: Document | HTMLElement,
  fields?: Record<string, string>[]
) => {
  const form = buildForm(container, fields)

  if (form) return { form }

  if (eventName === DATA_LAYER_EVENTS.LOGIN_SUCCESS) {
    return { user: buildUser() }
  }

  return {}
}

export const addDmpgEvent = (
  eventObject: DataLayerEvent,
  container?: Document | HTMLElement,
  fields?: Record<string, string>[],
  groupInfo = {}
) => {
  window.smDl.events.push({
    ...eventObject,
    ...eventExtras(eventObject.event, container || document, fields),
    ...groupInfo,
    user: buildUser(),
  })
}

export const updateDmpgSmdl = (event: DataLayerEvent) => {
  event.user = buildUser()
  window.smDl?.events.push(event)
}

export const clearSmdlCart = () => {
  if (window.smDl) {
    delete window.smDl.cart
    delete window.smDl.product
  }
}

export const setSovendousData = (sessionID: string) => {
  if (window.smDl) {
    window.smDl.platform.sessionId = btoa(sessionID) // base64 encoding
  }
}

export const triggerPlansSectionEvent = (plans: Plan[], category: string) => {
  const assets: SmdlAssets = {
    type: category,
    products: [],
  }
  const items: ModuleItem[] = []

  plans.forEach((plan, index) => {
    assets.products?.push({
      id: plan.slug,
      name: plan.slug,
      category: category,
      value: {
        displayTax: returnV2CalculatedTax(
          plan.finalPriceWithoutCredit?.value
        ).toFixed(2),
        displayGross: plan.finalPrice.value,
        chargeType: 'monthly',
      },
      discount: {
        coupon: getOfferName(plan),
        value: {
          displayGross: getDiscountValue(plan),
        },
      },
    })
    items.push({
      id: plan.slug,
      type: 'products',
      position: (index + 1).toString(),
    })
  })

  triggerEvent({
    assets,
    event: DATA_LAYER_EVENTS.MODULE_LOAD,
    modules: [
      {
        id: 'select-' + category + '-list',
        items,
        name: 'select-' + category + '-list',
        placement: 'full-page',
      },
    ],
    user: buildUser(),
  })
}
